import { forEachElement, getElementById, moveAllChildren, watchEvent } from "../util/Utils";
import { filter, filterFooter, filterSheetButton, nextBestActionButtons } from "./Selectors";
import { fireFilterSubmit, hasChanges, isForceSubmit } from "../filter/FilterFormActions";
import { FilterAccordion } from "../filter/FilterAccordion";
import { setActiveConfiguration, toggleFilterAccordion } from "@otto-ec/heureka-components/filter";
import { setFacetLocation } from "@otto-ec/heureka-components/facet";
import { type OcSheetV1Events, sheetV1 } from "@otto-ec/otto-components/sheet";
import { SvelteFacet } from "../filter/SvelteFacet";
import { eventQBus } from "../types/EventQBus";
import {
  copyLegacyLabels,
  readLegacyLabel,
  setLegacyLabels,
  updateLabel,
  updateLegacyLabel,
} from "../tracking/FeatureTracking";
import { registerClickTracker, sanFoldout } from "./FilterSheetTracking";
import { createEventMergeId, submitEventTracking } from "../tracking/TrackingUtils";
import { isActive } from "../toggle/Toggle";
import type { TrackingLabels } from "../tracking/TrackingLabels";

const FILTER_DETAIL_SHEET_ID = "heureka-filterdetailsheet";
const FILTER_DETAIL_SHEET_CONTENT_CLASS = "heureka_filterDetailSheet__content";
const FILTER_DETAIL_SHEET_ACTIONS_CLASS = "heureka_filterDetailSheet__actions";
const FILTER_DETAIL_SHEET_ACTIONS_NBF_CLASS = "heureka_filterDetailSheet__actions--nbf";
const FILTER_DETAIL_SHEET_CONTENT_SELECTOR = `.${FILTER_DETAIL_SHEET_CONTENT_CLASS}`;
const FILTER_DETAIL_SHEET_ACTIONS_SELECTOR = `.${FILTER_DETAIL_SHEET_ACTIONS_CLASS}`;

function removeFacetChangeNothing(elem: HTMLElement, event: Event, abortWatch: AbortController) {
  const target = event.target as HTMLInputElement;
  if (target.form && (hasChanges(target.form) || isForceSubmit(target.form))) {
    updateLegacyLabel(elem, "san_FacetChangeNothing");
    updateLegacyLabel(elem, "san_FacetSearchTerm");
    updateLegacyLabel(elem, "san_FacetSearchType");
    abortWatch.abort();
  }
}

export function addFormChangeWatcher(form: HTMLFormElement, elem: HTMLElement, resettable = true) {
  const facetChangeNothing = readLegacyLabel(elem, "san_FacetChangeNothing");
  copyLegacyLabels(form, elem);

  if (facetChangeNothing) {
    watchEvent(
      form,
      "input",
      (event, abortWatch) => {
        copyLegacyLabels(form, elem);
        removeFacetChangeNothing(elem, event, abortWatch);
      },
      resettable ? "submit" : undefined,
      () => {
        updateLegacyLabel(elem, "san_FacetChangeNothing", facetChangeNothing);
        copyLegacyLabels(form, elem);
      },
    );
  }
}

export default class OcFilterDetailSheet {
  /*               */

  constructor(
    private readonly filterId: string,
    readonly sheet: sheetV1 | undefined = getElementById(FILTER_DETAIL_SHEET_ID),
    private readonly accordion: FilterAccordion | undefined = FilterAccordion.filterId(filterId),
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(
      FILTER_DETAIL_SHEET_CONTENT_SELECTOR,
    ),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(
      FILTER_DETAIL_SHEET_ACTIONS_SELECTOR,
    ),
  ) {
    if (this.sheet) {
      this.sheet.headline = accordion?.filter?.filterTitle?.filterTitle;
    }
  }

  public static createForFilter(filterId: string) {
    return new OcFilterDetailSheet(filterId);
  }

  /*       */

  setSheet() {
    const form = filter(this.filterId);
    if (form) {
      if (this.sheetContent) {
        this.sheetContent.appendChild(form);
      }
      if (this.sheetActions) {
        moveAllChildren(filterFooter(form), this.sheetActions, true);
      }
    }
    return this;
  }

  setNextBestFilterSheet() {
    const form = filter(this.filterId);
    if (form) {
      if (this.sheetContent) {
        this.sheetContent.appendChild(form);
      }
      if (this.sheetActions) {
        this.sheetActions.classList.add(FILTER_DETAIL_SHEET_ACTIONS_NBF_CLASS);
        moveAllChildren(nextBestActionButtons(form), this.sheetActions, true);
      }
    }
    return this;
  }

  initializeNextBestFilterSheet() {
    if (this.sheet) {
      if (isActive("HEUREKA_2288_LAZY_FILTER_LOADING")) {
        toggleFilterAccordion(this.filterId, true);
      }
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      if (isActive("HEUREKA_2047_ADJUST_TRACKING")) {
        setFacetLocation("nbf_sheet", this.filterId);
      }
      this.sheet?.addEventListener("oc-sheet-after-open", this.afterOpen);
      this.sheet?.addEventListener("oc-sheet-before-close", this.beforeClose);

      this.open();

      this.refreshFacetChangeNothingLabel();

      const form = filter(this.filterId);
      if (this.sheetContent && form) {
        this.prepareContent(this.sheetContent, form, "close_nbf", "nbf");
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }
    }
    return this;
  }

  initializeSheet() {
    if (this.sheet) {
      if (isActive("HEUREKA_2288_LAZY_FILTER_LOADING")) {
        toggleFilterAccordion(this.filterId, true);
      }
      setActiveConfiguration("sheetSecondLevel", this.filterId);
      this.sheet?.addEventListener("oc-sheet-after-open", this.afterOpen);
      this.sheet?.addEventListener("oc-sheet-before-close", this.beforeClose);

      this.open();

      this.refreshFacetChangeNothingLabel();

      const form = filter(this.filterId);
      if (this.sheetContent && form) {
        this.prepareContent(this.sheetContent, form);
        updateLabel(form, "san_FilterFoldoutStatus", "opened");
      }
    }
    return this;
  }

  /*                       */

  private deactivate() {
    this.sheet?.removeEventListener("oc-sheet-after-open", this.afterOpen);
    this.sheet?.removeEventListener("oc-sheet-before-close", this.beforeClose);
    const source = this.sheetContent;
    if (source) {
      const detailSheetFacet = SvelteFacet.factory.pick(undefined, source);
      detailSheetFacet?.updateFeatureStatus("visible", "loaded");
    }

    const { accordion } = this;
    if (accordion) {
      accordion.filterForm = filter(this.filterId);
      if (this.sheetActions.classList.contains(FILTER_DETAIL_SHEET_ACTIONS_NBF_CLASS)) {
        this.sheetActions.classList.remove(FILTER_DETAIL_SHEET_ACTIONS_NBF_CLASS);
        accordion.nextBestActionButtons = this.sheetActions;
      } else {
        accordion.filterFooter = this.sheetActions;
      }
      accordion.filterFoldoutStatus = false;
    }

    return this;
  }

  open() {
    if (this.sheet) {
      this.sheet.open = true;
    }
    return this;
  }

  public back() {
    this.sheet?.back();
    return this;
  }

  /*       */

  private prepareContent(
    content: HTMLElement,
    form: HTMLFormElement,
    san_FilterFoldoutActivity: TrackingLabels["san_FilterFoldoutActivity"] = "close",
    san_FacetChangeNothing: TrackingLabels["san_FacetChangeNothing"] = "view_selection",
  ) {
    content.scrollTop = 0;
    content.dataset.filterId = this.filterId;

    addFormChangeWatcher(form, content, false);

    /*                                                                                   */
    setLegacyLabels(content, {
      san_FilterFoldoutType: this.filterId,
      san_FilterFoldoutActivity,
      san_FacetChangeNothing,
    });
  }

  private afterOpen = () => {
    registerClickTracker(this.sheet, {
      capture: true,
      passive: true,
    });
    eventQBus.emit("ftfind.filterSheet.openedDetail", { filterId: this.filterId });
  };

  private beforeClose = (closeEvent: OcSheetV1Events["oc-sheet-close"]) => {
    const form = filter(this.filterId);
    if (form instanceof HTMLFormElement) {
      form.dataset.tsFeatureFilterMethod = this.filterMethodFrom(closeEvent.detail.closeType);
      fireFilterSubmit(form);
    }
    this.deactivate();
    /*                                 */
    if (closeEvent.detail.closeType === "closedWithBackNavigation") {
      const { tsLabels } = this.sheetContent.dataset;
      const parsedLabels = JSON.parse(tsLabels);
      parsedLabels.san_FacetChangeNothing = "back";
      submitEventTracking(parsedLabels, createEventMergeId());
      eventQBus.emit("ftfind.filterSheet.closeDetail", { filterId: this.filterId });
      /*                                                */
      closeEvent.preventDefault();
    }
    /*                                       */
    if (closeEvent.detail.closeType === "closedWithX") {
      const { filterId, tsLabels } = this.sheetContent.dataset;
      const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
      eventQBus.emit("ftfind.filterSheet.closed", {
        filterId,
        tracking: {
          san_FilterWhatIf,
          san_Foldout: sanFoldout(closeEvent.detail.closeType),
          ...JSON.parse(tsLabels),
        },
      });
    }
  };

  public emitFilterSheetDetailClosedEvent() {
    if (this.sheet) {
      eventQBus.emit("ftfind.filterSheet.closedDetail", { filterId: this.filterId });
    }
    return this;
  }

  private refreshFacetChangeNothingLabel() {
    forEachElement(
      ".find_filterSheet__menuBackButton[type=submit]",
      (elem) => updateLegacyLabel(elem, "san_FacetChangeNothing", "adopt_down"),
      this.sheetActions,
    );
  }

  private filterMethodFrom(closeType: string): string {
    switch (closeType) {
      case "closedWithBackNavigation":
        return "adopt";
      case "closedProgrammatically":
        return "view_selection";
      default:
        return closeType;
    }
  }
}
